/**
 * @file: chat-threads.tsx
 *
 * Inbox wrapper around the chat thread list. Owns the "where does a tapped
 * chat thread go" decision: drive the shared `activeThread$` selection in
 * the desktop master-detail, else push the app-level chat flow on mobile (a
 * ROOT-stack surface, so the room isn't boxed in under the tab bar). A
 * thread tap is already resolved (the row carries the room), so it needs no
 * resolve gate. The list page itself (ChatThreadListView) stays
 * navigation-agnostic.
 */

import { useCurrentUser } from '@euler/app/flows/auth/context';
import {
  conversationHandleForThread,
  conversationIntentForThread,
} from '@euler/app/flows/chat/lib/conversation/types';
import { useOpenChatConversation } from '@euler/app/flows/chat/lib/navigation/openChatConversation';
import { useChatThreads } from '@euler/app/flows/chat/lib/threads/context';
import { ChatThreadListView } from '@euler/app/flows/chat/pages/thread-list';
import { ChatThreadItem } from '@euler/model/chat';
import { useBehaviorSubject } from '@euler/utils/hooks';
import { FC, memo, useCallback, useEffect, useRef } from 'react';
import { useInboxContext } from '../../context';
import { inboxConversation } from '../../types';

export const ChatThreads: FC = memo(() => {
  const { activeThread$, layoutMode$, infoOpen$ } = useInboxContext();
  const { chatThreads$ } = useChatThreads();
  const [activeThread] = useBehaviorSubject(activeThread$);
  const [layoutMode] = useBehaviorSubject(layoutMode$);
  const openConversation = useOpenChatConversation();
  const currentUser = useCurrentUser();
  const selfName = currentUser?.user?.name ?? null;

  const onOpenThread = useCallback(
    (thread: ChatThreadItem) => {
      if (layoutMode === 'desktop') {
        activeThread$.next(
          inboxConversation(conversationHandleForThread(thread, selfName)),
        );
        return;
      }
      openConversation(conversationIntentForThread(thread, selfName));
    },
    [activeThread$, layoutMode, openConversation, selfName],
  );

  const isActive = useCallback(
    (t: ChatThreadItem) =>
      activeThread?.type === 'conversation' &&
      activeThread.handle.kind === 'baked' &&
      activeThread.handle.roomId === t.room?.id,
    [activeThread],
  );

  // Desktop master-detail: when the open conversation's thread leaves the
  // list, deselect it (else the pane keeps showing a now-removed
  // conversation). One mechanism covers every way a thread can vanish —
  // local delete, a delete on another device, an owner dismiss, or being
  // kicked — since all of them drop the row from `chatThreads$`. Tracking the
  // rooms we've actually seen (rather than "active room not present") avoids
  // deselecting a freshly-opened bare chat that has no thread row yet. No-op
  // on mobile, where the list and conversation are separate screens.
  const seenRoomIds = useRef<Set<string>>(new Set());
  useEffect(() => {
    const sub = chatThreads$.subscribe(threads => {
      const roomIds = new Set(
        threads.map(t => t.room?.id).filter((id): id is string => id != null),
      );
      if (layoutMode === 'desktop') {
        const cur = activeThread$.getValue();
        const activeRoomId =
          cur?.type === 'conversation' && cur.handle.kind === 'baked'
            ? cur.handle.roomId
            : null;
        if (
          activeRoomId != null &&
          seenRoomIds.current.has(activeRoomId) &&
          !roomIds.has(activeRoomId)
        ) {
          activeThread$.next(null);
          infoOpen$.next(false);
        }
      }
      seenRoomIds.current = roomIds;
    });
    return () => sub.unsubscribe();
  }, [chatThreads$, activeThread$, infoOpen$, layoutMode]);

  return <ChatThreadListView isActive={isActive} onOpenThread={onOpenThread} />;
});
